import ConceptLinkerPhraseModel from 'models/ConceptLinkerPhraseModel';
import { ILinkedConcept } from 'types/conceptLinker';
import { tooltipLoader } from './TooltipLoader';

export interface IAiLinkerPhraseData {
  phrase: string;
  order: number;
  concepts: Array<{
    ref: number;
    refId: number;
    positions: number[];
    inflectStudied: string;
    deltaPos: number;
    name?: string;
    aiSelected?: boolean;
  }>;
}

export type AiLinkerOnUpdate = (engine: AiLinkerPhraseEngine) => void;

export class AiLinkerPhraseEngine {
  linkedConcepts: ILinkedConcept[] = [];
  allConcepts: ILinkedConcept[] = [];
  words: string[] = [];
  plain: string;
  order: number;

  private courseId: number;
  private targetDialectId: number;
  private entityRef: number;
  private entityRefId: number;
  private onUpdate: AiLinkerOnUpdate;
  private conceptLinkerPhraseModel: ConceptLinkerPhraseModel;

  constructor(opts: {
    courseId: number;
    targetDialectId: number;
    entityRef: number;
    entityRefId: number;
    phraseData: IAiLinkerPhraseData;
    onUpdate: AiLinkerOnUpdate;
  }) {
    this.courseId = opts.courseId;
    this.targetDialectId = opts.targetDialectId;
    this.entityRef = opts.entityRef;
    this.entityRefId = opts.entityRefId;
    this.onUpdate = opts.onUpdate;
    this.conceptLinkerPhraseModel = new ConceptLinkerPhraseModel();

    this.plain = opts.phraseData.phrase;
    this.order = opts.phraseData.order;
    this.words = opts.phraseData.phrase.split(/\s+/);

    // Initialize linked concepts from AI result
    this.linkedConcepts = (opts.phraseData.concepts || []).map((c) => ({
      ref: c.ref,
      refId: c.refId,
      positions: c.positions,
      inflectStudied: !!c.inflectStudied,
      deltaPos: c.deltaPos,
      preselected: false,
    }));
  }

  getCourse(): number {
    return this.courseId;
  }

  getTargetDialectId(): number {
    return this.targetDialectId;
  }

  getLinkedPositions(): number[] {
    const positions: number[] = [];
    this.linkedConcepts.forEach((c) => {
      positions.push(...c.positions);
    });
    return positions;
  }

  getAllConceptPositions(): number[] {
    const positions: number[] = [];
    this.allConcepts.forEach((c) => {
      positions.push(...c.positions);
    });
    return positions;
  }

  getLinkedConceptByPosition(position: number): ILinkedConcept | undefined {
    return this.linkedConcepts.find((c) => c.positions.indexOf(position) !== -1);
  }

  getGuessedConceptsByPosition(position: number): ILinkedConcept[] {
    return this.allConcepts.filter((c) => c.positions.indexOf(position) !== -1);
  }

  guess(fromScratch: boolean) {
    if (!this.plain) return;

    this.conceptLinkerPhraseModel
      .guessPhrases({
        ref: this.entityRef,
        refId: this.entityRefId,
        targetDialect: this.targetDialectId,
        phrase: this.plain,
        fromScratch,
      })
      .then((result) => {
        if (!result || Object.keys(result).length === 0) return;

        this.allConcepts = result.concepts || [];
        tooltipLoader?.preloadPhrases([{ concepts: this.allConcepts }], this.courseId, this.targetDialectId);

        if (result.exactMatch && result.possibleConcepts) {
          // Auto-link possible concepts that don't overlap with existing linked
          result.possibleConcepts.forEach((c) => {
            this.addLinkedConcept(c);
          });
        }

        this.onUpdate(this);
      });
  }

  invalidateConcept(concept: ILinkedConcept) {
    this.removeConceptFromLinked(concept);

    if (this.allConcepts.length === 0) {
      this.guess(true);
    }

    this.onUpdate(this);
  }

  selectConcept(concept: ILinkedConcept) {
    this.addLinkedConcept(concept);
    this.onUpdate(this);
  }

  private addLinkedConcept(concept: ILinkedConcept) {
    const linkedPositions = this.getLinkedPositions();
    const canBeAdded = concept.positions.every((pos) => linkedPositions.indexOf(pos) === -1);

    if (canBeAdded) {
      this.linkedConcepts.push(concept);
    }
  }

  private removeConceptFromLinked(removeConcept: ILinkedConcept) {
    const index = this.linkedConcepts.findIndex(
      (c) =>
        removeConcept.ref === c.ref &&
        removeConcept.refId === c.refId &&
        removeConcept.positions.length === c.positions.length &&
        JSON.stringify(removeConcept.positions.slice().sort()) === JSON.stringify(c.positions.slice().sort()),
    );

    if (index !== -1) {
      this.linkedConcepts.splice(index, 1);
    }
  }

  /** Export phrase data for persistence */
  toPhrasesData(): IAiLinkerPhraseData {
    return {
      phrase: this.plain,
      order: this.order,
      concepts: this.linkedConcepts.map((c) => ({
        ref: c.ref,
        refId: c.refId,
        positions: c.positions,
        inflectStudied: c.inflectStudied ? 'true' : '',
        deltaPos: c.deltaPos,
      })),
    };
  }
}
